// src/contexts/LanguageContext.tsx
import React, { createContext, useState, useContext, ReactNode } from 'react';

type Language = 'en' | 'mr';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string, defaultValue?: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// English translations
const translations = {
  en: {
    // Navigation
    'nav.benefits': 'Benefits',
    'nav.ourStory': 'About Us',
    'nav.specifications': 'Specifications',
    'nav.contact': 'Contact',
    
    // Hero Section
    'hero.tagline': '100% Organic Earth-Friendly',
    'hero.title': 'SAMSON ORGO',
    'hero.titleHighlight': 'Where Nature Meets Growth',
    'hero.description': 'Premium organic vermicompost crafted for sustainable agriculture growth and vibrant indoor gardens. Give your leafy friends the nutrients they deserve.',
    'hero.learnMore': 'Learn More',
    'hero.contactUs': 'Contact Us',
    
    // Target Audience
    'target.title': 'An excellent choice for cultivating:',
    'target.description': 'Our premium vermicompost is scientifically formulated to provide optimal nutrition for all types of plants, ensuring healthier growth and higher yields across different growing environments.',
    'target.veg': 'Vegetables',
    'target.fruits': 'Fruits',
    'target.flowers': 'Flowers',
    'target.lawns': 'Lawns',
    'target.nurseries': 'Nurseries',
    'target.pots': 'Pots',
    
    // Benefits Summary (Section)
    'benefits.title': 'Why Choose SAMSON ORGO?',
    'benefits.description': 'We believe in working with nature, not against it. Our vermicompost is designed to rejuvenate your soil from the inside out.',
    'benefits.viewAll': 'View all benefits',
    'benefits.organic.title': '100% Organic',
    'benefits.organic.desc': 'Pure natural ingredients sourced sustainably, containing zero synthetic additives or fillers.',
    'benefits.soil.title': 'Soil Health',
    'benefits.soil.desc': 'Enriches the microbiome and improves water retention for long-term plant vitality.',
    'benefits.chemical.title': 'Chemical-Free',
    'benefits.chemical.desc': 'Completely safe for your pets and children. No harmful residues or toxic odors.',
    'benefits.growth.title': 'Growth Boost',
    'benefits.growth.desc': 'See visible results in weeks with faster root development and greener foliage.',
    'benefits.page.title': 'Complete Benefits of Vermicompost',
    'benefits.page.description': 'Discover how our organic vermicompost transforms your soil and boosts plant health beyond conventional fertilizers.',
    
    // Detailed Benefits
    'detailed.title': 'Comprehensive Benefits of Vermicompost',
    'detailed.description': 'Unlike chemical fertilizers that provide temporary nutrition, vermicompost improves overall soil health and delivers long-term benefits for sustainable plant growth.',
    'detailed.fertility.title': 'Improves Soil Fertility',
    'detailed.fertility.desc': 'Enriches soil with essential macro and micronutrients that are released slowly, ensuring continuous SAMSON ORGOment without nutrient loss or soil degradation.',
    'detailed.structure.title': 'Enhances Soil Structure',
    'detailed.structure.desc': 'Increases soil porosity for better air circulation and root penetration. Improves water-holding capacity in sandy soils and reduces compaction in clay soils.',
    'detailed.microbial.title': 'Boosts Microbial Activity',
    'detailed.microbial.desc': 'Introduces beneficial microorganisms that break down organic matter, convert nutrients into plant-available forms, and suppress harmful pathogens.',
    'detailed.moisture.title': 'Improves Moisture Retention',
    'detailed.moisture.desc': 'Organic matter content helps soil retain water longer, reducing irrigation needs and protecting plants during dry conditions.',
    'detailed.healthy.title': 'Promotes Healthy Growth',
    'detailed.healthy.desc': 'Contains natural plant growth regulators (auxins, cytokinins) that stimulate root development and improve nutrient uptake.',
    'detailed.pollution.title': 'Reduces Soil Pollution',
    'detailed.pollution.desc': 'Restores soil health without toxic residues, improves organic carbon levels, and prevents soil erosion caused by chemical fertilizers.',
    'detailed.sustainable.title': 'Sustainable Agriculture',
    'detailed.sustainable.desc': 'Helps recycle organic waste, reduces environmental impact, and promotes eco-friendly farming practices.',
    'detailed.universal.title': 'Universal Application',
    'detailed.universal.desc': 'Safe for all crops, horticulture, home gardens, nurseries, lawns, and potted plants. Does not burn plants at any growth stage.',
    'detailed.cta.title': 'Why Choose Samson Orgo Fertilizer?',
    'detailed.cta.desc1': 'Our organic vermicompost is produced exclusively from cow dung using Eisenia Fetida earthworms. Rich in humus, NPK, micronutrients, and beneficial soil microbes including nitrogen-fixing and phosphate-solubilizing bacteria.',
    'detailed.cta.desc2': 'Scientifically proven to satisfy all parameters required for both growth promotion and plant protection. An excellent fit for farmers seeking sustainable, high-yield solutions.',
    'detailed.premium': 'Premium Quality',
    'detailed.lab': 'Lab Certified & Verified',


// Add to English translations object
'gallery.title': 'Our Gallery',
'gallery.subtitle': 'Explore visual stories of growth, sustainability, and success with our organic fertilizer',
'gallery.category.all': 'All',
'gallery.category.indoor': 'Indoor',
'gallery.category.agriculture': 'Agriculture',
'gallery.category.process': 'Process',
'gallery.category.greenhouse': 'Greenhouse',
'gallery.category.tutorial': 'Tutorial',
'gallery.category.testimonial': 'Testimonial',
'gallery.category.results': 'Results',
'gallery.category.sustainability': 'Sustainability',
'gallery.upload.title': 'Share Your Success!',
'gallery.upload.description': 'Have photos or videos of your plants thriving with our fertilizer? Share them with our community!',
'gallery.upload.button': 'Upload Media',
'gallery.upload.guidelines': 'View Guidelines',
'gallery.upload.note': 'Supports JPG, PNG, MP4 up to 50MB',
'gallery.empty.title': 'No media found',
'gallery.empty.message': 'Try selecting a different category',
'gallery.type.image': 'Image',
'gallery.type.video': 'Video',

    
    // Product Showcase
    'product.title': 'What is Vermicompost?',
    'product.secret': 'Our Secret?',
    'product.microbes': 'Healthy Microbes.',
    'product.descMain': 'Our premium vermicompost is harvested from red wigglers fed a diet of only Cow Dung which is nutrient-rich, easy for earthworms to digest, and helps create an ideal environment for fast decomposition. . The result is "black gold" — a nutrient powerhouse that plants crave.',
    'product.bullet1': 'Slow-release nutrients prevent fertilizer burn',
    'product.bullet2': 'Natural pest and disease resistance',
    'product.bullet3': 'Odorless and easy to apply indoors',
    'product.viewRange': 'View Product Range',
    
    // Testimonials
  'testimonial.title': 'What Our Customers Say',
'testimonial.rating': '★★★★★',

// Rajendra Patil
'testimonial.rajendra.name': 'Rajendra Patil',
'testimonial.rajendra.location': 'Farmer, Satara-Wai, Maharashtra',
'testimonial.rajendra.quote': '"I was sceptical at first when my neighbour suggested I switch from chemical fertilisers to vermicompost for my strawberry and vegetable farms along the Wai region. But within one season, the results were right in front of my eyes. My soil, which was becoming hard and dry year after year, became soft, dark, and rich again. The strawberries were juicier, the yield was higher, and my input costs dropped by nearly 40%. My family has been farming this land for three generations — and I can say with full confidence that vermicompost has given our soil a second life. Nature truly has all the answers."',


// Mehul Desai
'testimonial.mehul.name': 'Mehul Desai',
'testimonial.mehul.location': 'Businessman & Terrace Garden Enthusiast, Vapi, Gujarat',
'testimonial.mehul.quote': '"I run a chemical manufacturing unit in Vapi, so ironically, I am someone who understands exactly what synthetic inputs do to the environment over time. That understanding is precisely why I turned to vermicompost for my terrace garden at home — and later encouraged its use in the green belt around our factory premises. The transformation was nothing short of remarkable. My terrace plants — roses, herbs, tomatoes, and leafy greens — responded almost immediately. The leaves were greener, the flowering was more abundant, and the soil retained moisture far better through Vapi\'s brutal summers. What impressed me most was the total absence of foul odour that I had always associated with organic manure. Vermicompost is clean, odourless, easy to handle, and incredibly effective. I now source it in bulk and distribute it among my employees who have home gardens. If a man who works with chemicals every day trusts vermicompost completely — that should tell you something."',

// Thandiwe Nkosi
'testimonial.thandiwe.name': 'Thandiwe Nkosi',
'testimonial.thandiwe.location': 'Organic Farm Owner, Johannesburg, South Africa',
'testimonial.thandiwe.quote': '"Running an organic farm on the outskirts of Johannesburg comes with its own set of challenges — inconsistent rainfall, soil erosion, and the constant pressure to produce without compromising our organic certification. Vermicompost has been the single most impactful decision I have made for this farm in over a decade. We introduced vermicompost into our maize and spinach fields three seasons ago, and the difference has been extraordinary. Our soil structure has improved dramatically — water retention is up, and we are seeing far less runoff during the rainy season. Our crop yields have increased by close to 35%, and the nutritional quality of our produce has been validated through independent testing. What I love most is that vermicompost feeds the soil — not just the plant. It builds a living, breathing ecosystem beneath the surface that sustains itself season after season. For any African farmer looking to transition to sustainable agriculture, vermicompost is not just an option — it is the foundation."',

// Khalid Al Mansoori
'testimonial.khalid.name': 'Khalid Al Mansoori',
'testimonial.khalid.location': 'Landscape Consultant & Home Garden Enthusiast, Abu Dhabi, UAE',
'testimonial.khalid.quote': '"Growing anything in Abu Dhabi\'s desert climate is a labour of love. The heat is relentless; the soil is largely sandy and nutrient-deficient, and water conservation is not just a preference — it is a necessity. I have tried numerous soil amendments over the years, but nothing has delivered results as consistently and as impressively as vermicompost. I introduced vermicompost into my villa garden about two years ago, mixing it into the sandy soil before planting date palms, bougainvillea, and a small kitchen herb garden. The difference was visible within weeks. The soil began to bind and hold the structure. The plants showed vigorous, healthy growth without excessive watering. My herbs — mint, coriander, and basil — flourished through the summer heat in a way I had never seen before. For those of us living in arid climates where every drop of water and every grain of healthy soil is precious, vermicompost is genuinely a gift. It works with nature rather than against it — and in a desert, that philosophy is everything."',

// Priyanka Borthakur
'testimonial.priyanka.name': 'Priyanka Borthakur',
'testimonial.priyanka.location': 'Tea Garden Supervisor, Jorhat, Assam',
'testimonial.priyanka.quote': '"Assam\'s tea gardens are the pride of our state and the soul of our identity. Every cup of Assam tea that is enjoyed around the world carries with it the story of our soil, our climate, and our people. That is why, when our garden manager first proposed introducing vermicompost into our soil management programme, I supported it wholeheartedly — and I have never looked back. The tea bushes on our estate have responded to vermicompost in a way that years of chemical fertilisation have never achieved. The new flush growth — the tender two leaves and a bud that define the quality of Assam tea — is more consistent, more vibrant, and more aromatic than ever before. Our soil health scores have improved significantly, and we have seen a marked reduction in pest infestation, which I attribute to the stronger, more resilient root systems that vermicompost nurtures. Beyond the agronomics, there is something deeply satisfying about knowing that the tea we produce is now grown in harmony with the land, not at its expense. Vermicompost has made our garden not just more productive — but more sustainable, more ethical, and more true to the Assamese spirit of living with nature."',


    
    // Technical Specs
    'specs.title': 'Technical Specifications',
    'specs.description': 'Our vermicompost meets stringent quality standards with optimal parameters for superior plant growth and soil health.',
    'specs.physical': 'Physical Parameters',
    'specs.chemical': 'Chemical Parameters',
    'specs.biological': 'Biological Parameters',
    'specs.micro.title': 'Complete Micronutrient Profile',
    'specs.micro.desc': 'Rich in essential micronutrients for comprehensive plant nutrition',
    'specs.cert.title': 'Certified Quality Assurance',
    'specs.cert.desc': 'Our vermicompost is scientifically tested and certified by authorized agricultural laboratories in India, ensuring it meets all parameters required for both growth promotion and plant protection.',
    'specs.cert1': 'Free from toxic chemicals',
    'specs.cert2': 'Low heavy metal content',
    'specs.cert3': 'No weed seeds or pathogens',
    'specs.cert.organic': 'Certified Organic',
    'specs.cert.inst': 'Vasantdada Sugar Institute',
    'specs.cert.loc': 'Pune, India',
    
    // Footer
    'footer.description': 'Cultivating a greener future through premium organic waste transformation. Helping your indoor jungle thrive naturally.',
    'footer.quickLinks': 'Quick Links',
    'footer.shopAll': 'Shop All Products',
    'footer.wholesale': 'Wholesale Inquiry',
    'footer.careGuide': 'Plant Care Guide',
    'footer.subscription': 'Subscription Box',
    'footer.newsletter': 'Newsletter',
    'footer.newsletterText': 'Get plant care tips and exclusive offers.',
    'footer.emailPlaceholder': 'Your Email',
    'footer.copyright': '© {year} SAMSON ORGO Plants. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    
    // About Page (Expanded)
    'about.hero.title': 'About Us',
    'about.hero.desc': 'Cultivating a greener future through premium organic waste transformation',
    'about.mission.title': 'Our Mission',
    'about.mission.desc': 'To revolutionize agriculture by providing scientifically-proven organic solutions that respect nature\'s rhythm while delivering exceptional plant growth and soil health.',
    'about.vision.title': 'Our Vision',
    'about.vision.desc': 'To become India\'s most trusted brand for certified organic fertilizers, promoting sustainable agriculture and helping communities grow healthier food in harmony with nature.',
    'about.whatIs.title': 'What is Vermicompost?',
    'about.whatIs.p1': 'Vermicompost is a nutrient-rich, organic fertilizer and soil conditioner produced by the natural decomposition of organic materials (cow dung, food scraps, yard waste) using earthworms, typically red wigglers, and microorganisms.',
    'about.whatIs.p2': 'Often called "black gold," this humus-like material improves soil structure, enhances water retention, and provides essential, balanced nutrients for stronger plant growth and higher yields.',
    'about.whyInSoil.title': 'Why is vermicompost essential for the soil',
    'about.whyInSoil.item1.title': 'Improves Soil Fertility',
    'about.whyInSoil.item1.desc': 'Enriches soil with essential macro and micronutrients that are released slowly, ensuring continuous SAMSON ORGOment without nutrient loss.',
    'about.whyInSoil.item2.title': 'Enhances Soil Structure',
    'about.whyInSoil.item2.desc': 'Increases soil porosity for better air circulation and root penetration. Improves water-holding capacity in all soil types.',
    'about.whyInSoil.item3.title': 'Boosts Microbial Activity',
    'about.whyInSoil.item3.desc': 'Introduces beneficial microorganisms that break down organic matter and suppress harmful pathogens.',
    'about.whyInSoil.item4.title': 'Improves Moisture Retention',
    'about.whyInSoil.item4.desc': 'Organic matter content helps soil retain water longer, reducing irrigation needs by up to 40%.',
    'about.whyInSoil.item5.title': 'Reduces Soil Pollution',
    'about.whyInSoil.item5.desc': 'Restores soil health without toxic residues, improves organic carbon levels, and prevents soil erosion.',
    'about.whyInSoil.item6.title': 'Universal Application',
    'about.whyInSoil.item6.desc': 'Safe for all crops, horticulture, home gardens, nurseries, lawns, and potted plants at any growth stage.',
    'about.samson.title': 'Why Choose Samson Orgo Fertilizer?',
  'about.samson.p1': 
'Our organic vermicompost is produced exclusively from <cow>cow dung</cow> using <worm>Eisenia Fetida</worm> earthworms. Rich in humus, NPK, micronutrients, and beneficial soil microbes including nitrogen-fixing and phosphate-solubilizing bacteria.',
    'about.samson.p2': 'Scientifically proven to satisfy all parameters required for both growth promotion and plant protection. An excellent fit for farmers seeking sustainable, high-yield solutions.',
    'about.cert.title': 'Certified Quality',
    'about.cta': 'Back to Home',

    // Contact Page (Expanded)
    'contact.hero.title': 'Contact Us',
    'contact.hero.desc': 'Get in touch with our team for inquiries, support, and partnership opportunities',
    'contact.info.title': 'Get in Touch',
    'contact.info.location.title': 'Our Location',
    'contact.info.location.address': '123 Organic Lane, Greenfield, CA 90210, Maharashtra, India',
    'contact.info.phone.title': 'Phone Number',
    'contact.info.email.title': 'Email Address',
    'contact.info.hours.title': 'Business Hours',
    'contact.info.hours.week': 'Monday - Friday: 9:00 AM - 6:00 PM',
    'contact.info.hours.sat': 'Saturday: 10:00 AM - 4:00 PM',
    'contact.info.hours.sun': 'Sunday: Closed',
    'contact.info.social': 'Follow Us',
    'contact.form.title': 'Send us a Message',
    'contact.form.success': 'Thank you for your message! We\'ll get back to you within 24-48 hours.',
    'contact.form.error': 'Something went wrong. Please try again or email us directly at hello@SAMSON ORGOplants.com',
    'contact.form.name': 'Full Name *',
    'contact.form.email': 'Email Address *',
    'contact.form.phone': 'Phone Number',
    'contact.form.subject': 'Subject *',
    'contact.form.message': 'Your Message *',
    'contact.form.placeholderName': 'Enter your full name',
    'contact.form.placeholderEmail': 'Enter your email address',
    'contact.form.placeholderPhone': 'Enter your phone number',
    'contact.form.placeholderMessage': 'Please describe your inquiry in detail...',
    'contact.form.send': 'Send Message',
    'contact.form.sending': 'Sending Message...',
    'contact.form.subjects.select': 'Select a subject',
    'contact.form.subjects.product': 'Product Inquiry',
    'contact.form.subjects.wholesale': 'Wholesale Orders',
    'contact.form.subjects.support': 'Technical Support',
    'contact.form.subjects.partnership': 'Partnership Opportunity',
    'contact.form.subjects.feedback': 'Feedback & Suggestions',
    'contact.form.subjects.other': 'Other',
    'contact.faq.title': 'Frequently Asked Questions',
    'contact.faq.q1': 'How soon will I receive a response?',
    'contact.faq.a1': 'We typically respond within 24-48 hours during business days. For urgent inquiries, please call our support line.',
    'contact.faq.q2': 'Do you offer wholesale pricing?',
    'contact.faq.a2': 'Yes, we offer competitive wholesale pricing for bulk orders. Please select \'Wholesale Orders\' in the subject field above.',
    'contact.faq.q3': 'What is your shipping policy?',
    'contact.faq.a3': 'We ship across India with delivery times of 3-7 business days depending on location. International shipping is also available.',
    'contact.faq.q4': 'Can I visit your facility?',
    'contact.faq.a4': 'Yes, we welcome visitors by appointment. Please contact us to schedule a visit to our organic farm and production facility.',

    // Navbar
    'language.current': 'Language',
    'language.code': 'en',


    // Human Health Benefits - English
'health.title': 'Benefits of Vermicompost on Human Health',
'health.subtitle': 'Vermicompost is a natural organic fertilizer produced by earthworms that improves human health by promoting safe food, clean environments, and sustainable agriculture.',

'health.chemical.title': 'Chemical-Free & Safe Food Production ',
'health.chemical.desc1': 'Contains no synthetic chemicals, pesticides, or toxic residues.',
'health.chemical.desc2': 'Crops grown with vermicompost have lower chemical contamination, making food safer to eat.',
'health.chemical.desc3': 'Reduces long-term health risks such as cancer, hormonal imbalance, and nervous system disorders.',

'health.nutrition.title': 'Richer Nutritional Quality of Food ',
'health.nutrition.desc1': 'Enhances soil microbial activity, improving nutrient uptake by plants.',
'health.nutrition.desc2': 'Increases availability of essential nutrients like iron, calcium, magnesium, and zinc.',
'health.nutrition.desc3': 'Improves vitamin content and antioxidant levels in fruits and vegetables.',

'health.water.title': 'Protects Water & Public Health ',
'health.water.desc1': 'Releases nutrients slowly, preventing nitrate leaching into groundwater.',
'health.water.desc2': 'Helps protect drinking water from contamination.',
'health.water.desc3': 'Reduces risks of blue baby syndrome and digestive illnesses caused by polluted water.',

'health.farmers.title': 'Safer for Farmers & Communities ',
'health.farmers.desc1': 'Non-toxic and safe to handle, unlike chemical fertilizers.',
'health.farmers.desc2': 'Reduces occupational health problems such as skin irritation, respiratory issues, and poisoning.',
'health.farmers.desc3': 'Improves working conditions and long-term health of farmers.',

'health.gut.title': 'Supports Gut Health & Immunity ',
'health.gut.desc1': 'Promotes beneficial soil microbes that enhance plant health.',
'health.gut.desc2': 'Healthier crops contain more bioactive compounds that support human immunity.',
'health.gut.desc3': 'Indirectly improves gut health through consumption of cleaner, nutrient-dense food.',

'health.environmental.title': 'Reduces Environmental Diseases ',
'health.environmental.desc1': 'Lowers air, soil, and water pollution.',
'health.environmental.desc2': 'Decreases toxin accumulation in the food chain.',
'health.environmental.desc3': 'Helps prevent chronic diseases linked to environmental pollution.',

'health.conclusion': 'Vermicompost improves human health by producing safer, more nutritious food, protecting water resources, reducing chemical exposure, and creating a healthier environment.',

    // Add to English translations:
'gallery.pageTitle': 'Gallery',
'gallery.pageSubtitle': 'Visual stories of growth, sustainability, and success with our organic fertilizer',
'gallery.stats.images': 'Images',
'gallery.stats.videos': 'Videos',
'gallery.stats.organic': 'Organic',
'gallery.stats.access': 'Access',
'gallery.info.title': 'Share Your Green Journey',
'gallery.info.description': 'Our gallery is a community space where farmers and gardeners share their success stories. Join thousands of growers who have transformed their crops with Samson Orgo.',
'gallery.info.learnMore': 'Learn More About Us',
'gallery.info.contact': 'Contact for Media',
'nav.gallery': 'Gallery',
'nav.home': 'Back to Home',
  },

  
  
  mr: {
    // Navigation - मराठी
    'nav.benefits': 'फायदे',
    'nav.ourStory': 'आमची कथा',
    'nav.specifications': 'तपशील',
    'nav.contact': 'संपर्क',
    
    // Hero Section
    'hero.tagline': '१००% सेंद्रिय पृथ्वी-स्नेही',
    'hero.title': 'सॅमसन ऑर्गो',
    'hero.titleHighlight': 'जिथे निसर्गातून होते समृद्ध वाढ',
    'hero.description': 'शाश्वत शेतीची भरभराट आणि घरातील हिरव्या बागांची ताजेतवाने वाढ यासाठी खास तयार केलेले प्रीमियम सेंद्रिय गांडूळखत. आपल्या हिरव्या रोपांना समृद्ध वाढीसाठी आवश्यक सर्व पोषक द्रव्ये द्या.',
    'hero.learnMore': 'अधिक जाणून घ्या',
    'hero.contactUs': 'आमच्याशी संपर्क साधा',
    
    // Target Audience
    'target.title': 'परिपूर्ण सेंद्रिय वर्मीकंपोस्ट खत',
    'target.description': 'शास्त्रीय पद्धतीने तयार केलेले आमचे प्रीमियम वर्मीकंपोस्ट—वनस्पतींना संपूर्ण पोषण देऊन निरोगी वाढ आणि अधिक उत्पादनाची हमी!',
    'target.veg': 'भाज्या',
    'target.fruits': 'फळे',
    'target.flowers': 'फुले',
    'target.lawns': 'लॉन्स',
    'target.nurseries': 'रोपवाटिका (Nurseries)',
    'target.pots': 'कुंड्या (Pots)',
    
    // Benefits Summary
    'benefits.title': 'सॅमसन ऑर्गो का निवडा?',
    'benefits.description': 'आम्ही निसर्गाच्या विरोधात नाही, तर निसर्गासोबत काम करण्यावर विश्वास ठेवतो. आमचे वर्मीकंपोस्ट तुमच्या मातीचे आरोग्य आतून सुधारण्यासाठी बनवले आहे.',
    'benefits.viewAll': 'सर्व फायदे पहा',
    'benefits.organic.title': '१००% सेंद्रिय',
    'benefits.organic.desc': 'पूर्णपणे नैसर्गिक घटकांपासून बनवलेले, कोणत्याही रासायनिक भेसळीशिवाय.',
    'benefits.soil.title': 'मातीचे आरोग्य',
    'benefits.soil.desc': 'मातीतील सूक्ष्मजीवांचे प्रमाण वाढवते आणि पाणी धरून ठेवण्याची क्षमता सुधारते.',
    'benefits.chemical.title': 'केमिकल-मुक्त',
    'benefits.chemical.desc': 'तुमच्या पाळीव प्राण्यांसाठी आणि मुलांसाठी पूर्णपणे सुरक्षित. कोणताही हानिकारक वास नाही.',
    'benefits.growth.title': 'उत्तम वाढ',
    'benefits.growth.desc': 'काही आठवड्यांतच पानांचा हिरवागारपणा आणि मुळांच्या वाढीत बदल दिसून येईल.',
   'benefits.page.title': 'गांडूळखताचे संपूर्ण फायदे',
    'benefits.page.description': 'आमचे सेंद्रिय गांडूळखत मातीला नवजीवन देऊन तिची सुपीकता वाढवते आणि पारंपरिक खतांपेक्षा अधिक प्रभावीपणे वनस्पतींची जोमदार, निरोगी वाढ आणि भरघोस उत्पादन सुनिश्चित करते. फरक स्वतः अनुभवा!',

    // Detailed Benefits
    'detailed.title': 'वर्मीकंपोस्टचे सविस्तर फायदे',
    'detailed.description': 'रासायनिक खते तात्पुरते पोषण देतात, पण वर्मीकंपोस्ट मातीचे आरोग्य सुधारून दीर्घकालीन फायदे देते.',
    'detailed.fertility.title': 'मातीची सुपीकता वाढवते',
    'detailed.fertility.desc': 'मातीला आवश्यक पोषक घटक हळूहळू आणि सतत पुरवते, ज्यामुळे मातीचा दर्जा घसरत नाही.',
    'detailed.structure.title': 'मातीची रचना चांगली करते',
    'detailed.structure.desc': 'माती भुसभुशीत करते ज्यामुळे हवा खेळती राहते आणि मुळांना वाढायला जागा मिळते.',
    'detailed.microbial.title': 'सूक्ष्मजीवांची वाढ करते',
    'detailed.microbial.desc': 'उपयुक्त जिवाणूंची संख्या वाढवते जे सेंद्रिय पदार्थांचे खतात रूपांतर करतात.',
    'detailed.moisture.title': 'पाणी धरून ठेवण्याची क्षमता',
    'detailed.moisture.desc': 'माती पाणी जास्त काळ धरून ठेवते, ज्यामुळे पाण्याची गरज कमी होते.',
    'detailed.healthy.title': 'निरोगी वाढीस प्रोत्साहन',
    'detailed.healthy.desc': 'यात नैसर्गिक ग्रोथ रेग्युलेटर्स असतात जे मुळांच्या वाढीला चालना देतात.',
    'detailed.pollution.title': 'प्रदूषण कमी करते',
    'detailed.pollution.desc': 'कोणत्याही विषारी अंशांशिवाय माती पुन्हा जिवंत करते आणि धूप थांबवते.',
    'detailed.sustainable.title': 'शाश्वत शेती',
    'detailed.sustainable.desc': 'सेंद्रिय कचऱ्याचा पुनर्वापर करून पर्यावरणाचे रक्षण करते.',
    'detailed.universal.title': 'सर्व वनस्पतींसाठी उपयुक्त',
    'detailed.universal.desc': 'शेती, बागकाम, रोपवाटिका आणि कुंडीतील झाडांसाठी पूर्णपणे सुरक्षित.',
    'detailed.cta.title': 'सॅमसन ऑर्गो फर्टिलायझर का निवडावे?',
    'detailed.cta.desc1': 'आमचे सेंद्रिय वर्मीकंपोस्ट फक्त गाईच्या शेणापासून \'आयसेनिया फेटिडा\' गांडुळांचा वापर करून तयार केले जाते. यात एनपीके (NPK) आणि सूक्ष्म पोषक घटक भरपूर प्रमाणात असतात.',
    'detailed.cta.desc2': 'हे खत सरकारी प्रयोगशाळेतून प्रमाणित आहे आणि झाडांच्या वाढीसाठी व संरक्षणासाठी उत्तम आहे. शाश्वत शेतीसाठी हा एक उत्तम पर्याय आहे.',
    'detailed.premium': 'प्रीमियम गुणवत्ता',
    'detailed.lab': 'लॅब प्रमाणित आणि सत्यापित',

    // Product Showcase
    'product.title': 'वर्मीकंपोस्ट म्हणजे काय?',
    'product.secret': 'आमचे गुपित?',
    'product.microbes': 'निरोगी सूक्ष्मजीव.',
    'product.descMain': 'आमचे उच्च प्रतीचे गांडूळखत (वर्मीकंपोस्ट) लाल गांडुळांपासून तयार केले जाते, ज्यांना फक्त पोषक तत्वांनी समृद्ध गायीचे शेण दिले जाते. हे शेण गांडुळांसाठी सहज पचणारे असून जलद विघटनासाठी आदर्श वातावरण तयार करते.याचा परिणाम म्हणजे “ब्लॅक गोल्ड” — वनस्पतींसाठी अत्यंत पोषक आणि शक्तिशाली खत, जे त्यांच्या जोमदार वाढीसाठी आवश्यक आहे..',
    'product.bullet1': 'मंद गतीचे पोषण झाडांना जळण्यापासून वाचवते',
    'product.bullet2': 'कीड आणि रोगांविरुद्ध नैसर्गिक प्रतिकारशक्ती',
    'product.bullet3': 'बिनवासाचे आणि घरात वापरण्यास सोपे',
    'product.viewRange': 'उत्पादने पहा',


    // Add to Marathi translations object  
'gallery.title': 'आमची गॅलरी',
'gallery.subtitle': 'आमच्या गांडूळखत निर्मितीची संपूर्ण प्रक्रिया आणि उत्कृष्ट दर्जाच्या उत्पादनांची खास झलक',
'gallery.category.all': 'सर्व',
'gallery.category.indoor': 'इनडोअर',
'gallery.category.agriculture': 'शेती',
'gallery.category.process': 'प्रक्रिया',
'gallery.category.greenhouse': 'ग्रीनहाऊस',
'gallery.category.tutorial': 'ट्यूटोरियल',
'gallery.category.testimonial': 'अनुभव',
'gallery.category.results': 'निकाल',
'gallery.category.sustainability': 'टिकाऊपणा',
'gallery.upload.title': 'तुमचे यश शेअर करा!',
'gallery.upload.description': 'तुमच्या झाडांच्या यशाची छायाचित्रे किंवा व्हिडिओ आहेत का? आमच्या समुदायासोबत शेअर करा!',
'gallery.upload.button': 'मीडिया अपलोड करा',
'gallery.upload.guidelines': 'मार्गदर्शक तत्त्वे पहा',
'gallery.upload.note': 'JPG, PNG, MP4 (५० MB पर्यंत)',
'gallery.empty.title': 'मीडिया सापडले नाही',
'gallery.empty.message': 'दुसरी श्रेणी निवडण्याचा प्रयत्न करा',
'gallery.type.image': 'छायाचित्र',
'gallery.type.video': 'व्हिडिओ',

    // Testimonials
 'testimonial.title': 'आमच्या ग्राहकांचे अनुभव',
'testimonial.rating': '★★★★★',

// Rajendra Patil
'testimonial.rajendra.name': 'राजेंद्र पाटील',
'testimonial.rajendra.location': 'शेतकरी, सातारा-वाई, महाराष्ट्र',
'testimonial.rajendra.quote': '"अम्ही 15 वर्षांपासून शेतीचा व्यवसाय करीत आहोत, पण वर्मीकंपोस्ट वापरल्यानंतर आमच्या पिकाची क्वालिटी एकदम सुधारली! सुरुवातीला माझ्या शेजाऱ्याने वाई परिसरातील माझ्या स्ट्रॉबेरी आणि भाजीपाला शेतासाठी रासायनिक खतांऐवजी गांडूळखत वापरण्याची शिफारस केली तेव्हा मला शंका होती. पण एकाच हंगामात, परिणाम माझ्या डोळ्यांसमोर होते. माझी माती, जी वर्षानुवर्षे कडक आणि कोरडी होत होती, पुन्हा मऊ, काळी आणि समृद्ध झाली. स्ट्रॉबेरी रसाळ होत्या, उत्पादन जास्त होते आणि माझा खर्च जवळपास 40% कमी झाला. माझे कुटुंब तीन पिढ्यांपासून ही जमीन शेती करत आहे — आणि मी पूर्ण आत्मविश्वासाने सांगू शकतो की गांडूळखताने आमच्या मातीला दुसरे जीवन दिले आहे. निसर्गाकडे खरोखरच सर्व उत्तरे आहेत."',

// Mehul Desai
'testimonial.mehul.name': 'मेहुल देसाई',
'testimonial.mehul.location': 'उद्योजक & टेरेस गार्डन उत्साही, वापी, गुजरात',
'testimonial.mehul.quote': '"मी वापीमध्ये रासायनिक उत्पादन युनिट चालवतो, म्हणून उपरोधिकपणे, मी असा कोणी आहे जो कालांतराने सिंथेटिक इनपुट्स पर्यावरणाला काय करतात हे नक्की समजतो. ही समजच आहे की मी माझ्या घरच्या टेरेस गार्डनसाठी गांडूळखताकडे वळलो — आणि नंतर आमच्या कारखाना परिसरातील हरित पट्ट्यात त्याचा वापर प्रोत्साहित केला. परिवर्तन उल्लेखनीय होते. माझी टेरेस वनस्पती — गुलाब, औषधी वनस्पती, टोमॅटो आणि पालेभाज्या — जवळजवळ त्वरित प्रतिसाद दिला. पाने हिरवीगार होती, फुलांचा बहर अधिक होता, आणि वापीच्या भीषण उन्हाळ्यात माती ओलावा अधिक चांगला टिकवून ठेवत होती. मला सर्वात जास्त प्रभावित करणारी गोष्ट म्हणजे मी नेहमी सेंद्रिय खताशी जोडलेला दुर्गंधीचा पूर्ण अभाव. गांडूळखत स्वच्छ, गंधहीन, हाताळण्यास सोपे आणि अविश्वसनीय प्रभावी आहे. आता मी ते मोठ्या प्रमाणात खरेदी करतो आणि माझ्या कर्मचाऱ्यांमध्ये वाटतो ज्यांच्याकडे घरगुती बागा आहेत. जर एक माणूस जो दररोज रसायनांसह काम करतो तो गांडूळखतावर पूर्ण विश्वास ठेवतो — तर ते तुम्हाला काहीतरी सांगावे."',

// Thandiwe Nkosi
'testimonial.thandiwe.name': 'थांडिवे एनकोसी',
'testimonial.thandiwe.location': 'सेंद्रिय शेत मालक, जोहान्सबर्ग, दक्षिण आफ्रिका',
'testimonial.thandiwe.quote': '"जोहान्सबर्गच्या बाहेरील भागात सेंद्रिय शेत चालवणे ही आव्हानांची बाब आहे — अनियमित पाऊस, मातीची धूप, आणि आमचे सेंद्रिय प्रमाणपत्र धोक्यात न आणता उत्पादन करण्याचा सतत दबाव. गांडूळखत हा एका दशकात मी या शेतासाठी केलेला सर्वात प्रभावशाली निर्णय आहे. आम्ही तीन हंगामांपूर्वी आमच्या मका आणि पालक शेतात गांडूळखत सुरू केले, आणि फरक विलक्षण आहे. आमची माती रचना नाटकीयरीत्या सुधारली आहे — पाणी धारणा वाढली आहे, आणि पावसाळ्यात आम्ही खूप कमी वाहून जाणारे पाणी पाहत आहोत. आमचे पीक उत्पादन 35% च्या जवळ वाढले आहे, आणि आमच्या उत्पादनाची पौष्टिक गुणवत्ता स्वतंत्र चाचणीद्वारे प्रमाणित झाली आहे. मला सर्वात जास्त आवडते ते म्हणजे गांडूळखत मातीला पोषण देते — फक्त वनस्पतीला नाही. ते पृष्ठभागाखाली एक जिवंत, श्वास घेणारी परिसंस्था तयार करते जी स्वतःच हंगामानंतर हंगाम टिकवून ठेवते. शाश्वत शेतीकडे जाणाऱ्या कोणत्याही आफ्रिकन शेतकऱ्यासाठी, गांडूळखत फक्त एक पर्याय नाही — तो पाया आहे."',

// Khalid Al Mansoori
'testimonial.khalid.name': 'खालिद अल मन्सूरी',
'testimonial.khalid.location': 'लँडस्केप सल्लागार & होम गार्डन उत्साही, अबू धाबी, यूएई',
'testimonial.khalid.quote': '"अबू धाबीच्या वाळवंटी हवामानात काहीही वाढवणे हे प्रेमाचे काम आहे. उष्णता अटळ आहे; माती मुख्यतः वालुकामय आणि पोषक-कमी आहे, आणि पाणी संवर्धन ही फक्त प्राधान्य नाही — ती आवश्यकता आहे. मी वर्षानुवर्षे अनेक माती सुधारक वापरून पाहिले आहेत, पण गांडूळखताइतके सातत्याने आणि प्रभावीपणे परिणाम काहीही दिले नाहीत. मी सुमारे दोन वर्षांपूर्वी माझ्या विला गार्डनमध्ये गांडूळखत सुरू केले, खजूर, बोगनवेलिया आणि एक लहान औषधी वनस्पती बाग लावण्यापूर्वी ते वालुकामय मातीत मिसळले. फरक काही आठवड्यांतच दिसून आला. माती बांधली जाऊ लागली आणि रचना धरू लागली. वनस्पती जोमदार, निरोगी वाढ दर्शवित होत्या जास्त पाणी न देता. माझ्या औषधी वनस्पती — पुदिना, कोथिंबीर आणि तुळस — उन्हाळ्याच्या उष्णतेत अशा प्रकारे भरभराटल्या जसे मी यापूर्वी कधीही पाहिले नव्हते. आपल्यापैकी जे कोरड्या हवामानात राहतात जिथे पाण्याचा प्रत्येक थेंब आणि निरोगी मातीचा प्रत्येक कण मौल्यवान आहे, त्यांच्यासाठी गांडूळखत खरोखर एक भेट आहे. ते निसर्गाविरुद्ध न जाता निसर्गासोबत काम करते — आणि वाळवंटात, ती तत्वज्ञान सर्वकाही आहे."',

// Priyanka Borthakur
'testimonial.priyanka.name': 'प्रियांका बोरठाकूर',
'testimonial.priyanka.location': 'चहा बाग पर्यवेक्षक, जोरहाट, आसाम',
'testimonial.priyanka.quote': '"आसामच्या चहाच्या बागा आमच्या राज्याचा अभिमान आणि आमच्या ओळखीचा आत्मा आहेत. जगभरात आसाम चहाचा प्रत्येक कप जेव्हा प्याला जातो तेव्हा त्यात आमच्या मातीची, आमच्या हवामानाची आणि आमच्या लोकांची कथा असते. म्हणूनच, जेव्हा आमच्या बाग व्यवस्थापकाने प्रथम आमच्या माती व्यवस्थापन कार्यक्रमात गांडूळखत सुरू करण्याचा प्रस्ताव दिला, तेव्हा मी मनापासून समर्थन दिले — आणि मी कधीही मागे वळून पाहिले नाही. आमच्या इस्टेटवरील चहाच्या झुडपांनी गांडूळखताला अशा प्रकारे प्रतिसाद दिला आहे की वर्षानुवर्षे रासायनिक खताने कधीही साध्य केले नव्हते. नवीन पालवी — कोवळी दोन पाने आणि एक कळी जी आसाम चहाची गुणवत्ता ठरवते — पूर्वीपेक्षा अधिक सातत्यपूर्ण, अधिक जोमदार आणि अधिक सुगंधी आहे. आमच्या माती आरोग्य स्कोअरमध्ये लक्षणीय सुधारणा झाली आहे, आणि आम्ही कीड प्रादुर्भावात लक्षणीय घट पाहिली आहे, ज्याचे श्रेय मी मजबूत, अधिक लवचिक मूळ प्रणालींना देतो ज्या गांडूळखत पोषित करते. कृषीशास्त्रापलीकडे, आम्ही उत्पादित केलेला चहा आता जमिनीच्या किंमतीवर नव्हे तर त्याच्याशी सुसंगतपणे पिकवला जातो हे जाणून खोल समाधान आहे. गांडूळखताने आमची बाग फक्त अधिक उत्पादकच नाही तर — अधिक शाश्वत, अधिक नैतिक आणि निसर्गासोबत जगण्याच्या आसामी भावनेशी अधिक सत्य केली आहे."',

    // Technical Specs
    'specs.title': 'तांत्रिक तपशील',
    'specs.description': 'आमचे वर्मीकंपोस्ट उच्च दर्जाचे असून झाडांच्या वाढीसाठी आवश्यक सर्व तांत्रिक निकषांवर उरते.',
    'specs.physical': 'भौतिक निकष (Physical)',
    'specs.chemical': 'रासायनिक निकष (Chemical)',
    'specs.biological': 'जैविक निकष (Biological)',
    'specs.micro.title': 'सूक्ष्म पोषक घटकांचे प्रमाण',
    'specs.micro.desc': 'झाडांच्या सर्वांगीण पोषणासाठी आवश्यक असलेले सर्व सूक्ष्म पोषक घटक.',
    'specs.cert.title': 'प्रमाणित गुणवत्ता',
    'specs.cert.desc': 'आमचे खत भारतातील मान्यताप्राप्त कृषी प्रयोगशाळांद्वारे तपासले आणि प्रमाणित केले गेले आहे.',
    'specs.cert1': 'विषारी रसायनांपासून मुक्त',
    'specs.cert2': 'कमी जड धातूंचे प्रमाण',
    'specs.cert3': 'तण बिया किंवा रोगजनकांपासून मुक्त',
    'specs.cert.organic': 'प्रमाणित सेंद्रिय',
    'specs.cert.inst': 'वसंतदादा शुगर इन्स्टिट्यूट',
    'specs.cert.loc': 'पुणे, भारत',

    // Footer
    'footer.description': 'सेंद्रिय कचऱ्याचे रूपांतर करून हरित भविष्याची निर्मिती. तुमच्या झाडांना नैसर्गिकरित्या वाढण्यास मदत करणे.',
    'footer.quickLinks': 'द्रुत दुवे',
    'footer.shopAll': 'सर्व उत्पादने',
    'footer.wholesale': 'घाऊक चौकशी',
    'footer.careGuide': 'झाडांची काळजी मार्गदर्शक',
    'footer.subscription': 'सबस्क्रिप्शन बॉक्स',
    'footer.newsletter': 'वृत्तपत्रिका',
    'footer.newsletterText': 'झाडांच्या काळजीबद्दल टिप्स आणि विशेष ऑफर्स मिळवा.',
    'footer.emailPlaceholder': 'तुमचा ईमेल',
    'footer.copyright': '© {year} सॅमसन ऑर्गो प्लांट्स. सर्व हक्क राखीव.',
    'footer.privacy': 'गोपनीयता धोरण',
    'footer.terms': 'सेवा अटी',
    
    // About Page (Expanded) - मराठी
    'about.hero.title': 'आमची कथा',
    'about.hero.desc': 'प्रीमियम सेंद्रिय कचरा व्यवस्थापनाद्वारे हरित भविष्याची निर्मिती',
    'about.mission.title': 'आमचे ध्येय',
    'about.mission.desc': 'निसर्गाच्या लयाचा आदर करत आणि झाडांना उत्कृष्ट पोषण देत सेंद्रिय शेतीमध्ये क्रांती घडवून आणणे.',
    'about.vision.title': 'आमची दृष्टी',
    'about.vision.desc': 'भारतातील प्रमाणित सेंद्रिय खतांचा सर्वात विश्वासू ब्रँड बनणे, शाश्वत शेतीला प्रोत्साहन देणे आणि निसर्गाच्या सोबतीने निरोगी अन्न पिकवण्यास मदत करणे.',
    'about.whatIs.title': 'वर्मीकंपोस्ट म्हणजे काय?',
    'about.whatIs.p1': 'वर्मीकंपोस्ट हे एक पोषक तत्वांनी युक्त सेंद्रिय खत आहे जे गांडुळे (विशेषतः लाल गांडुळे) आणि सूक्ष्मजीवांच्या मदतीने सेंद्रिय पदार्थांच्या (गाईचे शेण, अन्नाचा कचरा) नैसर्गिक विघटनातून तयार केले जाते.',
    'about.whatIs.p2': 'याला अनेकदा "काळे सोने" म्हटले जाते. हे मातीची रचना सुधारते, पाण्याचा निचरा आणि साठवणूक क्षमता वाढवते आणि झाडांच्या जोमदार वाढीसाठी आवश्यक पोषण देते.',
    'about.whyInSoil.title': 'मातीमध्ये वर्मीकंपोस्ट का वापरले जाते?',
    'about.whyInSoil.item1.title': 'मातीची सुपीकता वाढवते',
    'about.whyInSoil.item1.desc': 'मातीला आवश्यक पोषक घटक हळूहळू आणि सतत पुरवते, ज्यामुळे पोषक तत्वांचा ऱ्हास होत नाही.',
    'about.whyInSoil.item2.title': 'मातीची रचना सुधारते',
    'about.whyInSoil.item2.desc': 'माती भुसभुशीत करते ज्यामुळे हवा खेळती राहते आणि मुळांना वाढायला जागा मिळते. सर्व प्रकारच्या मातीसाठी उपयुक्त.',
    'about.whyInSoil.item3.title': 'सूक्ष्मजीवांची वाढ',
    'about.whyInSoil.item3.desc': 'उपयुक्त जिवाणूंची संख्या वाढवते जे सेंद्रिय पदार्थांचे रूपांतर करतात आणि रोगांपासून संरक्षण करतात.',
    'about.whyInSoil.item4.title': 'ओलावा टिकवून धरते',
    'about.whyInSoil.item4.desc': 'माती पाणी जास्त काळ धरून ठेवते, ज्यामुळे पाण्याची गरज ४०% पर्यंत कमी होऊ शकते.',
    'about.whyInSoil.item5.title': 'प्रदूषण कमी करते',
    'about.whyInSoil.item5.desc': 'विषारी अंशांशिवाय माती पुन्हा जिवंत करते आणि जमिनीची धूप थांबवते.',
    'about.whyInSoil.item6.title': 'सर्वत्र उपयुक्त',
    'about.whyInSoil.item6.desc': 'शेती, फळबागा, घरगुती बागा, रोपवाटिका आणि कुंड्यांसाठी पूर्णपणे सुरक्षित.',
    'about.samson.title': 'सॅमसन ऑर्गो फर्टिलायझर का निवडावे?',
    'about.samson.p1': 
'आमचे सेंद्रिय वर्मीकंपोस्ट फक्त <cow>गाईच्या शेणापासून</cow> <worm>आयसेनिया फेटिडा</worm> गांडुळांचा वापर करून तयार केले जाते...',
    'about.samson.p2': 'हे खत शास्त्रीयदृष्ट्या प्रमाणित असून झाडांच्या वाढीसाठी आणि संरक्षणासाठी परिपूर्ण आहे. शाश्वत शेतीसाठी हा एक उत्तम पर्याय आहे.',
    'about.cert.title': 'प्रमाणित गुणवत्ता',
    'about.cta': 'मुख्य पृष्ठावर जा',

    // Contact Page (Expanded) - मराठी
    'contact.hero.title': 'संपर्क साधा',
    'contact.hero.desc': 'चौकशी, समर्थन किंवा भागीदारीच्या संधींसाठी आमच्या टीमशी संपर्क साधा',
    'contact.info.title': 'संपर्क माहिती',
    'contact.info.location.title': 'आमचा पत्ता',
    'contact.info.location.address': '१२३ सेंद्रिय लेन, ग्रीनफिल्ड, महाराष्ट्र, भारत',
    'contact.info.phone.title': 'फोन नंबर',
    'contact.info.email.title': 'ईमेल पत्ता',
    'contact.info.hours.title': 'कामाची वेळ',
    'contact.info.hours.week': 'सोमवार - शुक्रवार: सकाळी ९ ते संध्याकाळी ६',
    'contact.info.hours.sat': 'शनिवार: सकाळी १० ते दुपारी ४',
    'contact.info.hours.sun': 'रविवार: सुट्टी',
    'contact.info.social': 'सोशल मीडियावर फॉलो करा',
    'contact.form.title': 'आम्हाला संदेश पाठवा',
    'contact.form.success': 'तुमच्या संदेशाबद्दल धन्यवाद! आम्ही २४-४८ तासांच्या आत तुमच्याशी संपर्क साधू.',
    'contact.form.error': 'काहीतरी चूक झाली. कृपया पुन्हा प्रयत्न करा किंवा आम्हाला थेट ईमेल करा.',
    'contact.form.name': 'पूर्ण नाव *',
    'contact.form.email': 'ईमेल पत्ता *',
    'contact.form.phone': 'फोन नंबर',
    'contact.form.subject': 'विषय *',
    'contact.form.message': 'तुमचा संदेश *',
    'contact.form.placeholderName': 'तुमचे पूर्ण नाव टाका',
    'contact.form.placeholderEmail': 'तुमचा ईमेल पत्ता टाका',
    'contact.form.placeholderPhone': 'तुमचा फोन नंबर टाका',
    'contact.form.placeholderMessage': 'तुमची विचारणा सविस्तर सांगा...',
    'contact.form.send': 'संदेश पाठवा',
    'contact.form.sending': 'संदेश पाठवत आहे...',
    'contact.form.subjects.select': 'विषय निवडा',
    'contact.form.subjects.product': 'उत्पादन चौकशी',
    'contact.form.subjects.wholesale': 'घाऊक ऑर्डर',
    'contact.form.subjects.support': 'तांत्रिक मदत',
    'contact.form.subjects.partnership': 'भागीदारीची संधी',
    'contact.form.subjects.feedback': 'प्रतिक्रिया आणि सूचना',
    'contact.form.subjects.other': 'इतर',
    'contact.faq.title': 'सतत विचारले जाणारे प्रश्न',
    'contact.faq.q1': 'मला किती वेळात प्रतिसाद मिळेल?',
    'contact.faq.a1': 'आम्ही सहसा कामाच्या दिवसात २४-४८ तासांत प्रतिसाद देतो. तातडीच्या चौकशीसाठी कृपया आम्हाला कॉल करा.',
    'contact.faq.q2': 'तुम्ही घाऊक दरात खत देता का?',
    'contact.faq.a2': 'हो, आम्ही घाऊक दरात खत पुरवतो. कृपया वरील फॉर्ममध्ये \'घाऊक ऑर्डर\' निवडा.',
    'contact.faq.q3': 'तुमची डिलिव्हरी पॉलिसी काय आहे?',
    'contact.faq.a3': 'आम्ही संपूर्ण भारतात ३-७ दिवसांत डिलिव्हरी देतो. आंतरराष्ट्रीय डिलिव्हरी देखील उपलब्ध आहे.',
    'contact.faq.q4': 'मी तुमच्या उत्पादन केंद्राला भेट देऊ शकतो का?',
    'contact.faq.a4': 'हो, तुम्ही पूर्वपरवानगी घेऊन आमच्या केंद्राला भेट देऊ शकता. भेटीची वेळ निश्चित करण्यासाठी आमच्याशी संपर्क साधा.',


    // Human Health Benefits - Marathi
'health.title': 'मानवी आरोग्यावर गांडूळखताचे फायदे',
'health.subtitle': 'गांडूळखत हे गांडुळांद्वारे तयार केलेले नैसर्गिक सेंद्रिय खत आहे, जे सुरक्षित अन्ननिर्मिती, स्वच्छ पर्यावरण आणि शाश्वत शेतीला प्रोत्साहन देऊन मानवी आरोग्य सुधारते.',

'health.chemical.title': 'रसायनमुक्त आणि सुरक्षित अन्न उत्पादन',
'health.chemical.desc1': 'गांडूळखतात कोणतेही कृत्रिम रसायने, कीटकनाशके किंवा विषारी अवशेष नसतात.',
'health.chemical.desc2': 'गांडूळखत वापरून पिकवलेल्या पिकांमध्ये रासायनिक दूषितता कमी असते, त्यामुळे अन्न अधिक सुरक्षित होते.',
'health.chemical.desc3': 'कर्करोग, हार्मोन्स असंतुलन आणि मज्जासंस्थेच्या विकारांसारखे दीर्घकालीन आरोग्यधोके कमी होतात.',

'health.nutrition.title': 'अन्नातील पोषणमूल्य वाढवते ',
'health.nutrition.desc1': 'मातीतील सूक्ष्मजीवांची क्रिया वाढवून वनस्पतींच्या पोषकतत्त्व शोषणात सुधारणा करते.',
'health.nutrition.desc2': 'लोह, कॅल्शियम, मॅग्नेशियम आणि झिंक यांसारख्या आवश्यक घटकांची उपलब्धता वाढवते.',
'health.nutrition.desc3': 'फळे आणि भाज्यांतील जीवनसत्त्वे व अँटिऑक्सिडंट्सचे प्रमाण वाढवते.',

'health.water.title': 'पाणी आणि सार्वजनिक आरोग्याचे संरक्षण ',
'health.water.desc1': 'पोषकतत्त्वे हळूहळू सोडल्यामुळे भूजलात नायट्रेट मिसळण्याचे प्रमाण कमी होते.',
'health.water.desc2': 'पिण्याच्या पाण्याचे प्रदूषण टाळण्यास मदत होते.',
'health.water.desc3': 'दूषित पाण्यामुळे होणारे आजार (जसे ब्लू बेबी सिंड्रोम, पचनसंस्थेचे विकार) कमी होतात.',

'health.farmers.title': 'शेतकरी आणि समाजासाठी सुरक्षित ',
'health.farmers.desc1': 'रासायनिक खतांच्या तुलनेत सुरक्षित आणि विषमुक्त आहे.',
'health.farmers.desc2': 'त्वचेची जळजळ, श्वसनाचे त्रास आणि विषबाधा यांसारखे व्यावसायिक आरोग्यधोके कमी करते.',
'health.farmers.desc3': 'शेतकऱ्यांचे कामाचे वातावरण आणि दीर्घकालीन आरोग्य सुधारते.',

'health.gut.title': 'पचनसंस्था आणि रोगप्रतिकारशक्ती वाढवते ',
'health.gut.desc1': 'मातीतील उपयुक्त सूक्ष्मजीव वाढवून वनस्पतींचे आरोग्य सुधारते.',
'health.gut.desc2': 'निरोगी पिकांमध्ये रोगप्रतिकारशक्ती वाढवणारे घटक अधिक प्रमाणात असतात.',
'health.gut.desc3': 'स्वच्छ आणि पोषक अन्नामुळे अप्रत्यक्षपणे पचनसंस्थेचे आरोग्य सुधारते.',

'health.environmental.title': 'पर्यावरणीय आजार कमी करते ',
'health.environmental.desc1': 'हवा, माती आणि पाण्याचे प्रदूषण कमी करते.',
'health.environmental.desc2': 'अन्नसाखळीत विषारी घटकांचे संचयन कमी करते.',
'health.environmental.desc3': 'प्रदूषणामुळे होणारे दीर्घकालीन आजार टाळण्यास मदत करते.',

'health.conclusion': 'गांडूळखत सुरक्षित आणि पोषक अन्ननिर्मिती करून, पाण्याचे संरक्षण करून, रसायनांचा संपर्क कमी करून आणि स्वच्छ पर्यावरण निर्माण करून मानवी आरोग्य सुधारते.',



    // Navbar
    'language.current': 'भाषा',
    'language.code': 'mr',
    // English:
    
   // Add to Marathi translations:
'gallery.pageTitle': 'गॅलरी',
'gallery.pageSubtitle': 'आमच्या सेंद्रिय खताच्या वाढ, टिकाऊपणा आणि यशाच्या दृश्य कथा',
'gallery.stats.images': 'छायाचित्रे',
'gallery.stats.videos': 'व्हिडिओ',
'gallery.stats.organic': 'सेंद्रिय',
'gallery.stats.access': 'प्रवेश',
'gallery.info.title': 'तुमचे हरित प्रवास शेअर करा',
'gallery.info.description': 'आमची गॅलरी ही एक समुदाय जागा आहे जिथे शेतकरी आणि बागकाम करणारे त्यांच्या यशाच्या कथा शेअर करतात. हजारो लोकांमध्ये सामील व्हा ज्यांनी सॅमसन ऑर्गोसह त्यांची पिके बदलली आहेत.',
'gallery.info.learnMore': 'आमच्याबद्दल अधिक जाणून घ्या',
'gallery.info.contact': 'मीडियासाठी संपर्क करा',
'nav.gallery': 'गॅलरी',
'nav.home': 'मुख्य पृष्ठावर जा',



    // Add to your en translations object


  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'mr' : 'en');
  };

  const t = (key: string, defaultValue?: string): string => {
  const langTranslations: Record<string, string> = translations[language];
  const translation = langTranslations[key];

  let text = translation || defaultValue || key;

  if (key === 'footer.copyright') {
    text = text.replace('{year}', new Date().getFullYear().toString());
  }

  // Replace custom tags with styled spans
  text = text
    .replace(
      /<cow>(.*?)<\/cow>/g,
      '<span class="bg-emerald-200 text-emerald-900 px-2 py-1 rounded font-semibold">$1</span>'
    )
    .replace(
      /<worm>(.*?)<\/worm>/g,
      '<span class="italic font-semibold text-emerald-900 underline decoration-emerald-400 underline-offset-4">$1</span>'
    );

  return text;
};

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};